import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef } from 'react';
import Cookies from 'js-cookie';
import { authAPI } from '../services/api';
import { toast } from '../utils/toast';
import { authenticatePasskey, passkeyWasCancelled, passkeyErrorMessage } from '../utils/passkey';

// Initial state
const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  loading: true,
  error: null,
  twoFactor: {
    required: false,
    tempToken: null,
    email: null
  }
};

// Actions
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
  SET_TWO_FACTOR: 'SET_TWO_FACTOR',
  CLEAR_TWO_FACTOR: 'CLEAR_TWO_FACTOR'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        loading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        loading: false
      };
    
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    
    case AUTH_ACTIONS.SET_TWO_FACTOR:
      return {
        ...state,
        twoFactor: {
          required: true,
          tempToken: action.payload.tempToken,
          email: action.payload.email
        }
      };
    
    case AUTH_ACTIONS.CLEAR_TWO_FACTOR:
      return {
        ...state,
        twoFactor: {
          required: false,
          tempToken: null,
          email: null
        }
      };
    
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Store tokens in cookies. The standalone tablets site keeps the refresh
  // token much longer ("remember this device" for dispatch stations).
  const storeTokens = useCallback((accessToken, refreshToken) => {
    const isTabletsHost = window.location.hostname.toLowerCase().startsWith('tablets.');
    Cookies.set('accessToken', accessToken, { 
      expires: 1, // 1 day
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict'
    });
    
    if (refreshToken) {
      Cookies.set('refreshToken', refreshToken, { 
        expires: isTabletsHost ? 90 : 30, // days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
    }
  }, []);

  // Clear tokens from cookies
  const clearTokens = useCallback(() => {
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
  }, []);

  // Login function (Step 1 of 2FA)
  const login = useCallback(async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await authAPI.login(credentials);
      
      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        // Set 2FA state in context
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        dispatch({ 
          type: AUTH_ACTIONS.SET_TWO_FACTOR, 
          payload: {
            tempToken: response.data.tempToken,
            email: response.data.email
          }
        });
        return response.data;
      }

      // Legacy: Direct login (fallback if 2FA not implemented)
      const { user, accessToken, refreshToken } = response.data;

      // Store tokens
      storeTokens(accessToken, refreshToken);

      // Update state
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, accessToken, refreshToken }
      });

      toast.success(`Welcome back, ${user.username}!`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [storeTokens]);

  // Passkey sign-in. Does not flip the global loading flag: that unmounts the
  // login page and can cancel the browser's passkey prompt.
  const loginWithPasskey = useCallback(async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
      const options = await authAPI.passkeyLoginOptions();
      const assertion = await authenticatePasskey(options.data);
      const response = await authAPI.passkeyLoginVerify({ response: assertion });
      const { user, accessToken, refreshToken } = response.data;

      storeTokens(accessToken, refreshToken);
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, accessToken, refreshToken }
      });
      toast.success(`Welcome back, ${user.username}!`);
      return response.data;
    } catch (error) {
      if (passkeyWasCancelled(error)) return null;
      const errorMessage = passkeyErrorMessage(error, 'Passkey sign-in failed');
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, [storeTokens]);

  // Verify 2FA code (Step 2 of 2FA)
  const verify2FA = useCallback(async ({ tempToken, code }) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await authAPI.verify2FA({ tempToken, code });
      const { user, accessToken, refreshToken } = response.data;

      // Store tokens
      storeTokens(accessToken, refreshToken);

      // Clear 2FA state and update login state
      dispatch({ type: AUTH_ACTIONS.CLEAR_TWO_FACTOR });
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, accessToken, refreshToken }
      });

      toast.success(`Welcome back, ${user.username}!`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '2FA verification failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error; // Don't show toast here, let component handle it
    }
  }, [storeTokens]);

  // Resend 2FA code
  const resend2FA = useCallback(async ({ tempToken }) => {
    try {
      const response = await authAPI.resend2FA({ tempToken });
      toast.success('New verification code sent to your email');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend verification code';
      throw error; // Let component handle the error
    }
  }, []);

  // Logout function
  const logout = useCallback(async (showMessage = true) => {
    try {
      // Try to logout from server
      await authAPI.logout();
    } catch (error) {
      // Ignore server errors during logout
      console.warn('Logout error:', error);
    } finally {
      // Always clear local state
      clearTokens();
      dispatch({ type: AUTH_ACTIONS.CLEAR_TWO_FACTOR });
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      if (showMessage) {
        toast.info('You have been logged out');
      }
    }
  }, [clearTokens]);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      const token = Cookies.get('accessToken');
      
      if (!token) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      // Get complete user data including custom tag filters
      const response = await authAPI.me();
      const userData = response.data.user;

      console.log('DEBUG: User data from /me endpoint:', userData);
      console.log('DEBUG: Custom tag filters in response:', userData.customTagFilters);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: userData,
          accessToken: token,
          refreshToken: Cookies.get('refreshToken')
        }
      });
    } catch (error) {
      // Only clear tokens if it's an auth error (401), not rate limiting (429)
      if (error.response?.status === 401) {
        clearTokens();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } else {
        // For other errors (like 429), just set loading to false
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    }
  }, [clearTokens]);

  // Track whether user was authenticated (for toast logic on session expiry)
  const wasAuthenticatedRef = useRef(false);
  useEffect(() => {
    wasAuthenticatedRef.current = !!state.user;
  }, [state.user]);

  // Listen for session-expired events from the API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      clearTokens();
      dispatch({ type: AUTH_ACTIONS.CLEAR_TWO_FACTOR });
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      if (wasAuthenticatedRef.current) {
        toast.warning('Your session has expired. Please log in again.');
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [clearTokens]);

  // Initialize authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Proactive token refresh and tab-visibility handling
  useEffect(() => {
    if (!state.user) return;

    const attemptSilentRefresh = async () => {
      try {
        const rt = Cookies.get('refreshToken');
        if (!rt) return;
        const response = await authAPI.silentRefresh({ refreshToken: rt });
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        storeTokens(accessToken, newRefreshToken);
      } catch (error) {
        if (error.response?.status === 401) {
          clearTokens();
          dispatch({ type: AUTH_ACTIONS.CLEAR_TWO_FACTOR });
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
          toast.warning('Your session has expired. Please log in again.');
        }
      }
    };

    // Refresh access token every 20 hours (it expires at 24h)
    const refreshTimer = setInterval(attemptSilentRefresh, 20 * 60 * 60 * 1000);

    // When tab becomes visible again, proactively refresh to catch stale sessions
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        attemptSilentRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(refreshTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [!!state.user, storeTokens, clearTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh token
  const refreshAuthToken = useCallback(async () => {
    try {
      const refreshToken = Cookies.get('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authAPI.refresh({ refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Store new tokens
      storeTokens(accessToken, newRefreshToken);

      // Update state
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: state.user,
          accessToken,
          refreshToken: newRefreshToken
        }
      });

      return accessToken;
    } catch (error) {
      // Refresh failed, logout user
      logout(false);
      throw error;
    }
  }, [state.user, storeTokens, logout]);

  // Change password
  const changePassword = useCallback(async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      toast.success('Password changed successfully');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to change password';
      toast.error(errorMessage);
      throw error;
    }
  }, []);

  // Update user profile
  const updateUser = useCallback((userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData
    });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Clear 2FA state
  const clearTwoFactor = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_TWO_FACTOR });
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return state.user?.role === role || state.user?.isAdmin;
  }, [state.user]);

  // Check if user has permission for page
  const hasPagePermission = useCallback((pageId) => {
    if (!state.user) return false;
    if (state.user.isAdmin) return true;
    return state.user.permissions?.includes(pageId);
  }, [state.user]);

  // Get current user's token
  const getToken = useCallback(() => {
    return state.token || Cookies.get('accessToken');
  }, [state.token]);

  // Check if user can manage pages
  const canManagePages = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canManagePages;
  }, [state.user]);

  // Check if user can access accounting
  const canAccessAccounting = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessAccounting;
  }, [state.user]);

  // Check if user can access QuickBooks
  const canAccessQuickBooks = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessQuickBooks;
  }, [state.user]);

  // Check if user can access DocSign
  const canAccessDocSign = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessDocSign;
  }, [state.user]);

  // Check if user can view public form applications
  const canAccessApplications = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessApplications;
  }, [state.user]);

  // Check if user can access Water Usage
  const canAccessWaterUsage = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessWaterUsage;
  }, [state.user]);

  // Check if user can access Insurance (editing is checked by the server,
  // which returns canManage with the policy list)
  const canAccessInsurance = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessInsurance;
  }, [state.user]);

  // Check if user can access the Cameras page (live NVR video)
  const canAccessCameras = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessCameras;
  }, [state.user]);

  // Check if user can access the Traffic page (HTTPS proxy request stats)
  const canAccessTraffic = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessTraffic;
  }, [state.user]);

  // Check if user can access the Dropbox page (own files, company dropbox,
  // share links)
  const canAccessDropbox = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessDropbox;
  }, [state.user]);

  // Check if user can access the Doors page (portal and tablets site). Its
  // own permission, not a Driver Tablets sub-tab.
  const canAccessDoors = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessDoors;
  }, [state.user]);

  // Check if user can access the Fuel page (fuel card purchases)
  const canAccessFuel = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessFuel;
  }, [state.user]);

  // Check if user can post/organize the driver portal's Driver docs
  const canManagePortalDocs = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canManagePortalDocs;
  }, [state.user]);

  // Check if user can access Driver Analytics
  const canAccessDriverAnalytics = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessDriverAnalytics;
  }, [state.user]);

  // Check if user can access Driver Tablets
  const canAccessDriverTablets = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canAccessDriverTablets;
  }, [state.user]);

  // Check if user can view a linked driver's credential documents
  const canViewDriverDocuments = useCallback(() => {
    if (!state.user) return false;
    return state.user.isAdmin || state.user.canViewDriverDocuments;
  }, [state.user]);

  // Check if user has a Driver Tablets sub-permission (Manage tab or nav item)
  const canSeeTabletTab = useCallback((tabKey) => {
    if (!state.user) return false;
    if (state.user.isAdmin) return true;
    if (!state.user.canAccessDriverTablets) return false;
    return Array.isArray(state.user.tabletTabs) && state.user.tabletTabs.includes(tabKey);
  }, [state.user]);

  // Memoize the context value to prevent unnecessary re-renders of all consumers
  // Only re-creates when the actual state or action references change
  const value = useMemo(() => ({
    // State
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    isAuthenticated: !!state.user,
    isAdmin: state.user?.isAdmin || false,
    twoFactor: state.twoFactor,
    
    // Actions
    login,
    loginWithPasskey,
    verify2FA,
    resend2FA,
    logout,
    checkAuth,
    refreshAuthToken,
    changePassword,
    updateUser,
    clearError,
    clearTwoFactor,
    
    // Utilities
    hasRole,
    hasPagePermission,
    canManagePages,
    canAccessAccounting,
    canAccessQuickBooks,
    canAccessDocSign,
    canAccessApplications,
    canAccessWaterUsage,
    canAccessInsurance,
    canAccessCameras,
    canAccessTraffic,
    canAccessDropbox,
    canAccessDoors,
    canAccessFuel,
    canManagePortalDocs,
    canAccessDriverAnalytics,
    canAccessDriverTablets,
    canViewDriverDocuments,
    canSeeTabletTab,
    getToken
  }), [
    state.user, state.token, state.loading, state.error, state.twoFactor,
    login, loginWithPasskey, verify2FA, resend2FA, logout, checkAuth, refreshAuthToken,
    changePassword, updateUser, clearError, clearTwoFactor,
    hasRole, hasPagePermission, canManagePages, canAccessAccounting,
    canAccessQuickBooks, canAccessDocSign, canAccessApplications, canAccessWaterUsage, canAccessInsurance, canAccessCameras, canAccessTraffic, canAccessDropbox, canAccessDoors, canAccessFuel, canManagePortalDocs, canAccessDriverAnalytics,
    canAccessDriverTablets, canViewDriverDocuments, canSeeTabletTab, getToken
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext; 